import * as models from "./models";
import getUserIds from "../getUserIds";
import instance from "./instance";
import formatDateString from "../../lib/formatDateString";
import {AxiosResponse} from "axios";

const DEFAULT_ATTEMPT = 1;

export function sendError(data: models.ErrorData): Promise<void> {
    return instance
        .request({
            url: "/error",
            method: "POST",
            data,
        })
        .then((response) => response.data);
}

export function invitationValidate(
    invitationCode: string,
    zipCode: string,
): Promise<models.InviteValidation> {
    return instance
        .request({
            url: "/invitation/validate",
            method: "POST",
            data: {
                invitationCode,
                zipCode,
            },
        })
        .then((response) => response.data);
}

export function applicationLead(
    fields: models.FieldChangeInfo[],
    invitationCode: string | null,
    url: string,
): Promise<string> {
    return instance
        .request({
            url: "/application/lead",
            method: "POST",
            data: {
                ...getUserIds(),
                invitationCode,
                url,
                fields,
            },
        })
        .then((response) => response.data);
}

export function applicationValidate(
    data: models.ApplicationValidate,
): Promise<string | models.ApplicationValidationResponse> {
    return instance
        .request({
            url: "/application/validate",
            method: "POST",
            data: {
                ...getUserIds(),
                ...data,
                attempt: DEFAULT_ATTEMPT,
            },
        })
        .then((response) => response.data);
}

export function applicationSubmit(
    data: models.ApplicationValidate,
    econsent: boolean,
    eCbsvConsent: boolean,
    nscConsent?: boolean,
): Promise<models.ApplicationSubmitResponse> {
    return instance
        .request({
            url: "/application/submit",
            method: "POST",
            data: {
                ...getUserIds(),
                ...data,
                econsent: econsent,
                eCbsvConsent: eCbsvConsent,
                nscConsent: nscConsent,
                attempt: DEFAULT_ATTEMPT,
            },
        })
        .then((response) => response.data);
}

export function applicationSecurity(
    applicationId: string,
    mothersMaidenName?: string,
    contactName?: string,
    contactRelationship?: string,
    contactAddress?: string,
    contactHomePhone?: string,
    contactPermission?: boolean,
): Promise<boolean> {
    return instance
        .request({
            url: "/application/security",
            method: "POST",
            data: {
                [models.AppSecurityKeys.ApplicationId]: applicationId,
                [models.AppSecurityKeys.MothersMaidenName]: mothersMaidenName,
                [models.AppSecurityKeys.ContactName]: contactName,
                [models.AppSecurityKeys.ContactRelationship]: contactRelationship,
                [models.AppSecurityKeys.ContactAddress]: contactAddress,
                [models.AppSecurityKeys.ContactHomePhone]: contactHomePhone,
                [models.AppSecurityKeys.ContactPermission]: contactPermission,
            },
        })
        .then((response) => response.data);
}

export function getInstantCard(): Promise<models.InstantCard> {
    return instance
        .request({url: "/instant-card", method: "GET"})
        .then((response) => response.data);
}

export function collegeSearch(search: string): Promise<string[]> {
    const encodedSearch = encodeURIComponent(search);
    return instance
        .request({
            url: `/application/collegeSearch/${encodedSearch}`,
            method: "GET",
        })
        .then((response) => response.data);
}

export function visit(
    visitId: string,
    url: string,
    agent?: string,
    referrer?: string,
): Promise<string> {
    return instance
        .request({
            url: "/visit",
            method: "POST",
            data: {
                visitId,
                url,
                agent,
                referrer,
            },
        })
        .then((response) => response.data);
}

export function forgotInvite(
    data: models.ForgotInfo,
    visitId: string,
): Promise<models.InviteValidation> {
    return instance
        .request({
            url: "/invitation/forgot",
            method: "POST",
            data: {
                ...data,
                visitId,
                [models.AppKeys.DateOfBirth]: formatDateString(data.dob),
            },
        })
        .then((response) => response.data);
}

export function getCardTypes(landingPage: string): Promise<models.GetCardTypesResponse> {
    return instance
        .request({
            url: "/invitation/digitalOffer",
            method: "POST",
            data: {
                landingPage,
            },
        })
        .then((response) => response.data);
}

export function review(data: models.Review): Promise<void> {
    return instance
        .request({
            url: "/application/review",
            method: "POST",
            data: {
                text: data.text,
                title: data.title,
                name: data.email,
                invitationCode: data.invitationCode,
                rating: data.rating,
            },
        })
        .then((response) => response.data);
}

/* Secured Card Deposit */
export function securedBankDeposit(
    amount: number,
    saveInfo: boolean,
    data: models.SecuredBankInfo,
): Promise<string> {
    return instance
        .request({
            url: "/secured-card/bank-deposit",
            method: "POST",
            data: {
                ...data,
                amount,
                [models.SecuredDepositKeys.Save]: saveInfo,
            },
        })
        .then((response) => response.data);
}

export function securedCardDeposit(
    amount: number,
    saveInfo: boolean,
    data: models.SecuredCardInfo,
): Promise<string> {
    return instance
        .request({
            url: "/secured-card/card-deposit",
            method: "POST",
            data: {
                ...data,
                amount,
                [models.SecuredDepositKeys.Save]: saveInfo,
            },
        })
        .then((response) => response.data);
}

export function securedCardReminder(data: models.SecuredReminderInfo): Promise<string> {
    return instance
        .request({url: "/secured-card/reminder", method: "POST", data})
        .then((response) => response.data);
}

export function postSecured(decision: string): Promise<AxiosResponse> {
    return instance
        .request({
            url: "/secured-card",
            method: "POST",
            data: {
                decision,
            },
        })
        .then((response) => response);
}

export function getApplication(
    data?: models.LookupAppInfo,
): Promise<models.ApplicationSubmitResponse> {
    if (data) {
        return instance
            .request({
                url: "/application",
                method: "GET",
                params: {
                    ...data,
                    [models.LookupAppKeys.DateOfBirth]: formatDateString(data.dob),
                },
            })
            .then((response) => response.data);
    } else {
        // Use Authorization Cookie
        return instance
            .request({
                url: "/application",
                method: "GET",
            })
            .then((response) => response.data);
    }
}
export function counterOffer(): Promise<models.CounterOffer> {
    return instance
        .request({
            url: "/counter-offer",
            method: "GET",
        })
        .then((response) => response.data);
}

export function viewedReferAFriend(): Promise<models.ShortCode> {
    return instance
        .request({
            url: "/refer-a-friend/refer/view",
            method: "POST",
        })
        .then((response) => response.data);
}

export function referAFriendDecision(maybeLater: boolean, shortCode: string): Promise<void> {
    return instance
        .request({
            url: "/refer-a-friend/refer/",
            method: "POST",
            data: {
                maybeLater,
                shortCode,
            },
        })
        .then((response) => response.data);
}

export function referAFriendValidate(token: string): Promise<models.ShortCode> {
    return instance
        .request({
            url: "/refer-a-friend/validate",
            method: "GET",
            params: {
                token,
            },
        })
        .then((response) => response.data);
}

export function viewedRedeemAFriend(shortCode: string): Promise<models.Referral> {
    return instance
        .request({
            url: `/refer-a-friend/redeem/${shortCode}`,
            method: "GET",
            params: {
                visitId: getUserIds().visitID,
            },
        })
        .then((response) => response.data);
}

export function redeemAFriendDecision(
    acceptMyReward: boolean,
    shortCode: string,
): Promise<models.Referral> {
    return instance
        .request({
            url: `/refer-a-friend/redeem/${shortCode}`,
            method: "POST",
            data: {
                acceptMyReward,
                visitId: getUserIds().visitID,
            },
        })
        .then((response) => response.data);
}

export function forgotSecuredCard(email: string): Promise<void> {
    return instance
        .request({
            url: "/application/forgot",
            method: "POST",
            data: {
                [models.AppKeys.Email]: email,
            },
        })
        .then((response) => response.data);
}
